/*
 * Copyright (C) 2020 Graylog, Inc.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the Server Side Public License, version 1,
 * as published by MongoDB, Inc.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * Server Side Public License for more details.
 *
 * You should have received a copy of the Server Side Public License
 * along with this program. If not, see
 * <http://www.mongodb.com/licensing/server-side-public-license>.
 */
import * as React from 'react';
import { useRef } from 'react';

import usePluginEntities from 'hooks/usePluginEntities';
import type { EntitySharedAction, ModalHandler } from 'components/permissions/types';
import useHasEntityOwnership from 'hooks/useHasEntityOwnership';

function usePluggableEntitySharedActions<T>(entity: T, entityType: string, onCloseModal = undefined) {
  const modalRefs = useRef({});
  const pluginActions = usePluginEntities('components.shared.entityActions');
  const hasOwnership = useHasEntityOwnership((entity as T & { id: string })?.id, entityType);

  const availableActions = pluginActions.filter((action) => (action.useCondition ? !!action.useCondition() : true));

  // Only actions the user can actually use are returned, so callers can tell from the length whether
  // anything will render and avoid showing an empty menu.
  const actions = (hasOwnership ? availableActions : []).map((action: EntitySharedAction<T, ModalHandler>) => {
    const { key, component: PluggableEntityAction } = action;

    return (
      <PluggableEntityAction key={`entity-action-${key}`} entity={entity} modalRef={() => modalRefs.current[key]} />
    );
  });

  const actionModals = availableActions
    .filter(({ modal }) => !!modal)
    .map((action) => {
      const { key, modal: ActionModal } = action;

      return (
        <ActionModal
          entity={entity}
          entityType={entityType}
          onClose={onCloseModal}
          key={`entity-action-modal-${key}`}
          ref={(ref) => {
            modalRefs.current[key] = ref;
          }}
        />
      );
    });

  return { actions, actionModals };
}

export default usePluggableEntitySharedActions;
